import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, ArrowRight, Target, Award, Users } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Target size={28} />,
      title: 'Independent',
      description: 'Unbiased advice with no conflicts of interest. Your project success is our sole focus.',
    },
    {
      icon: <Award size={28} />,
      title: 'Engineering-Led',
      description: 'Technical rigour underpins everything we do, ensuring decisions are grounded in data and best practice.',
    },
    {
      icon: <Users size={28} />,
      title: 'Pragmatic',
      description: 'No-nonsense approach that balances technical excellence with commercial reality.',
    },
  ];

  const differentiators = [
    'End-to-end support from concept through to exit',
    'Engineering expertise combined with commercial acumen',
    'Deep understanding of Irish and EU built environment context',
    'Data-driven decision-making at every project stage',
    'Proven track record in optimizing project viability',
    'Independent perspective free from contractor or supplier bias',
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#0d1b2e] text-white py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#4a9ece] to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fadeIn">
              About Downes Engineering and Consulting
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Engineering-led advisory services for property developers, investors, and asset owners who demand rigorous, independent expertise.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/373912/pexels-photo-373912.jpeg"
                alt="Professional engineering consultancy"
                className="rounded-lg shadow-xl w-full h-[500px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-6">
                Our Approach
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Downes Engineering and Consulting was founded to provide property developers and investors with truly independent, engineering-led advisory services across the built environment.
              </p>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Our principal consultant brings extensive experience in pre-development, cost management, and commercial advisory, with a track record of improving project outcomes from feasibility through to completion and beyond.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We work across Ireland and the wider EU, supporting clients who value rigorous analysis, pragmatic advice, and a consultant who understands both the engineering and commercial dimensions of development.
              </p>
              <Link to="/services">
                <Button className="bg-[#4a9ece] hover:bg-[#3a8ebe] text-white">
                  Our Services
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-4">
              Our Values
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The principles that guide every project and client relationship
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-slate-200 hover:border-[#4a9ece] hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4a9ece] text-white rounded-full mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-[#0d1b2e] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-6">
                What Makes Us Different
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                In a crowded advisory market, we stand out through our commitment to independence, engineering rigour, and pragmatic commercial sense. We're not tied to contractors, suppliers, or product manufacturers — our only interest is your project's success.
              </p>
              <ul className="space-y-4">
                {differentiators.map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#4a9ece] flex-shrink-0 mt-0.5" size={24} />
                    <span className="text-slate-700 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1546349851-64285be8e9fa"
                alt="Engineering excellence"
                className="rounded-lg shadow-xl w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b2e]/20 to-transparent rounded-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0d1b2e] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Let's Discuss Your Next Project
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Whether you're at concept stage or mid-project, we're here to provide the independent expertise you need.
          </p>
          <Link to="/contact">
            <Button className="bg-[#4a9ece] hover:bg-[#3a8ebe] text-white px-10 py-6 text-lg">
              Get in Touch
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
